/**
 * Infinite Scroll
 * ===============
 * Loads next page of posts automatically when user scrolls near the bottom.
 * Works with Ghost's pagination by fetching the next page URL and
 * appending the post cards to the existing grid.
 *
 * Only activates on pages that have a .gh-pagination element with a next link.
 */

export default function infiniteScroll() {
    const pagination = document.querySelector('.gh-pagination');
    if (!pagination) return;

    const nextLink = pagination.querySelector('.gh-pagination-next');
    if (!nextLink) return;

    const grid = document.querySelector('.gh-card-grid');
    if (!grid) return;

    let nextUrl = nextLink.href;
    let isLoading = false;

    // Hide pagination UI — we'll handle it with scroll
    pagination.style.display = 'none';

    // Create loading indicator
    const loader = document.createElement('div');
    loader.className = 'gh-infinite-loader';
    loader.innerHTML = '<div class="gh-infinite-spinner"></div>';
    loader.style.display = 'none';
    grid.parentNode.insertBefore(loader, grid.nextSibling);

    // Intersection observer for triggering load
    const sentinel = document.createElement('div');
    sentinel.className = 'gh-infinite-sentinel';
    grid.parentNode.insertBefore(sentinel, loader);

    const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && !isLoading && nextUrl) {
                loadMore();
            }
        },
        {
            rootMargin: '300px', // Start loading before user reaches the bottom
        }
    );

    observer.observe(sentinel);

    async function loadMore() {
        if (!nextUrl || isLoading) return;
        isLoading = true;
        loader.style.display = '';

        try {
            const response = await fetch(nextUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Get new cards
            const newCards = doc.querySelectorAll('.gh-card-grid .gh-card');
            newCards.forEach((card) => {
                grid.appendChild(document.importNode(card, true));
            });

            // Get next page URL
            const newNextLink = doc.querySelector('.gh-pagination-next');
            if (newNextLink) {
                nextUrl = newNextLink.href;
            } else {
                nextUrl = null;
                observer.disconnect();
                sentinel.remove();
            }
        } catch (err) {
            console.error('Infinite scroll load failed:', err);
            // Show pagination as fallback
            pagination.style.display = '';
            observer.disconnect();
            sentinel.remove();
        } finally {
            isLoading = false;
            loader.style.display = 'none';
        }
    }
}
